import {
  UPDATE_DATA_DUMP_FOR_PROCESS,
  UPDATE_MANY_DATA_DUMPS_FOR_PROCESSES,
} from "providers/GlobalStateProvider";
import { Fragment, useEffect, useRef } from "react";
import DataDumpGetter from "segments/DataDumpGetter/DataDumpGetter";
import {
  useFocusRange,
  useInterval,
  useIsSerialProcess,
  useSelectedProcesses,
  useVisibleProcessIds,
} from "utils/customHooks/genericHooks";
import { useForceRerenderOnIntervalIfCurrentRange } from "./useForceRerenderOnIntervalIfCurrentRange";
import { useProcessIdsForDataDumpFullRange } from "./useProcessIdsForDataDumpFullRange";
import { useDispatch } from "react-redux";
import { DataDumpAggregateType } from "types/types";
import { useCleanRawDataDump } from "segments/DataDumpGetter/dataDumpGetterUtils";

export function DataDumpGetterForFullRangePerProcess() {
  const focusRange = useFocusRange();
  const processIdsForDataDumpFullRange =
    useProcessIdsForDataDumpFullRange(false);

  const isSerialProcess = useIsSerialProcess();
  const subordinateProcessIds = useSubordinateProcessIds();

  const randKey = useForceRerenderOnIntervalIfCurrentRange();

  // We only add 1 second to `lt` if it's the same time as `ge`.
  // There have been issues where the time is 1 second greater than 1 day and 24-hour reports stop working.
  const lessThan = focusRange?.lt ?? 0;
  const adjustedLessThan =
    (focusRange?.ge ?? 0) < lessThan ? lessThan : lessThan + 1;

  // dispatching the aggregates to the store causes a major re-render of many components.
  // so, to stabilize the render, we'll only dispatch once, when all the data is ready.
  const DISPATCH_INTERVAL = 5 * 1000;
  const processDataDumpsRef = useRef<{
    dataDumpsObj: { [processId: string]: DataDumpAggregateType[] };
    lastUpdate: number;
  }>({
    lastUpdate: Date.now(),
    dataDumpsObj: {},
  });
  // empty the bars whenever time range changes (these bars are now stale)
  useEffect(() => {
    processDataDumpsRef.current = {
      dataDumpsObj: {},
      lastUpdate: Date.now(),
    };
  }, [focusRange]);
  const dispatch = useDispatch();
  useInterval({
    callback: () => {
      // check on an interval if we're ready to dispatch yet
      const numDataDumps = Object.keys(
        processDataDumpsRef.current.dataDumpsObj
      ).length;
      const allDataPresent =
        numDataDumps === processIdsForDataDumpFullRange.length;
      const isNewDataReady = numDataDumps > 0;
      const timeElapsed = Date.now() - processDataDumpsRef.current.lastUpdate;
      const ready =
        allDataPresent || (isNewDataReady && timeElapsed > DISPATCH_INTERVAL);
      if (ready) {
        dispatch({
          type: UPDATE_MANY_DATA_DUMPS_FOR_PROCESSES,
          payload: processDataDumpsRef.current.dataDumpsObj,
        });
        processDataDumpsRef.current = {
          ...processDataDumpsRef.current,
          dataDumpsObj: {},
          lastUpdate: Date.now(),
        };
      }
    },
    delay: DISPATCH_INTERVAL,
    immediate: false,
  });
  const cleanRawDataDump = useCleanRawDataDump();

  return (
    <Fragment key={randKey}>
      {[
        ...processIdsForDataDumpFullRange,
        ...(isSerialProcess ? subordinateProcessIds : []),
      ].map(processId => (
        <DataDumpGetter
          key={processId}
          timeRange={{ ...focusRange, lt: adjustedLessThan } as any}
          processIds={[processId]}
          dataDumpAggType={UPDATE_DATA_DUMP_FOR_PROCESS}
          additionalPayload={{ processId }}
          initialDelayMs={Math.round(Math.random() * 5000)}
          customOnCompleted={data => {
            processDataDumpsRef.current = {
              ...processDataDumpsRef.current,
              dataDumpsObj: {
                ...(processDataDumpsRef.current.dataDumpsObj || {}),
                [processId]: cleanRawDataDump(data.dataDumpAggregate, [
                  processId,
                ]),
              },
            };
          }}
        />
      ))}
    </Fragment>
  );
}

function useSubordinateProcessIds() {
  const visibleProcessIds = useVisibleProcessIds();
  const selectedProcessIdsRef = useSelectedProcesses();

  return visibleProcessIds.filter(
    processId => !selectedProcessIdsRef.current.includes(processId)
  );
}
